import { Badge } from "../ui/badge";
import { Loader2 } from "lucide-react";
import { ProcessStatus } from "../../types/process";
import { ActionItemStatus } from "../../types/actionItems";
import React from "react";

interface StatusBadgeProps {
  status: ProcessStatus | ActionItemStatus;
  className?: string;
  showSpinner?: boolean;
}

export function StatusBadge({ status, className = "", showSpinner = true }: StatusBadgeProps) {
  const isProcessStatus = (status: ProcessStatus | ActionItemStatus): status is ProcessStatus => {
    return ['queued', 'processing', 'completed', 'error'].includes(status);
  };

  const getStatusConfig = (status: ProcessStatus | ActionItemStatus): { variant: "default" | "secondary" | "destructive" | "success" | "warning"; label: string } => {
    if (isProcessStatus(status)) {
      switch (status) {
        case 'queued':
          return { variant: "secondary", label: "Queued" };
        case 'processing':
          return { variant: "default", label: "Processing" };
        case 'completed':
          return { variant: "success", label: "Completed" };
        case 'error':
          return { variant: "destructive", label: "Error" };
        default:
          return { variant: "secondary", label: status };
      }
    } else {
      // Action Item statuses
      switch (status) {
        case 'todo':
          return { variant: "secondary", label: "To Do" }; // Gray like Queued
        case 'in_progress':
          return { variant: "default", label: "In Progress" }; // Blue like Processing
        case 'on_hold':
          return { variant: "warning", label: "On Hold" }; // Yellow from badge.tsx
        case 'done':
          return { variant: "success", label: "Done" }; // Green like Completed
        default:
          return { variant: "secondary", label: status };
      }
    }
  };

  const config = getStatusConfig(status);

  return (
    <Badge 
      variant={config.variant}
      className={`px-2.5 py-0.5 text-[11px] font-medium leading-none capitalize whitespace-nowrap flex items-center gap-1 ${className}`}
    >
      {status === 'processing' && showSpinner && (
        <Loader2 className="h-3 w-3 animate-spin" />
      )}
      {config.label}
    </Badge>
  );
}